import React, { useEffect, useState } from "react";
import i18n from "@/utils/i18n";
import { useTranslation } from "react-i18next";
import Drawer from "@/components/ui/drawer";
import Chip from "@/components/ui/chip";
import FileImage from "@/components/ui/file-image";
import { DocumentModal, Calendar } from "@/components/icons";
import type { AnnouncementListItemApi, AnnouncementStatus } from "../types/announcements.types";
import { announcementsApi, apiStatusToUi } from "../services/announcements.api";
import styles from "./AnnouncementDetailDrawer.module.scss";

const STATUS_LABELS: Record<string, string> = {
  taslak: "Taslak",
  yayinda: i18n.t("announcementsStatus.yayinda"),
  kaldirildi: i18n.t("announcementsStatus.kaldirildi"),
  suresi_dolmus: i18n.t("announcementsStatus.suresi_dolmus"),
};

const STATUS_CHIP_TYPE: Record<AnnouncementStatus, "info" | "error" | "warning"> = {
  taslak: "warning",
  yayinda: "info",
  kaldirildi: "error",
  suresi_dolmus: "error",
};

/** ISO 8601 → DD.MM.YYYY (timezone-safe: sadece tarih kısmı kullanılır) */
function formatDate(iso: string | undefined): string {
  if (!iso?.trim()) return "—";
  const datePart = iso.slice(0, 10);
  const match = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return "—";
  const [, y, m, d] = match;
  return `${d}.${m}.${y}`;
}

export interface AnnouncementDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  announcementId: string | null;
}

const AnnouncementDetailDrawer: React.FC<AnnouncementDetailDrawerProps> = ({
  isOpen,
  onClose,
  announcementId,
}) => {
  const { t } = useTranslation();
  const [announcement, setAnnouncement] = useState<AnnouncementListItemApi | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !announcementId) {
      setAnnouncement(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    announcementsApi
      .getById(announcementId)
      .then((data) => {
        if (!cancelled) setAnnouncement(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message ?? i18n.t("announcementsCommon.loadFailed"));
          setAnnouncement(null);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, announcementId]);

  if (!announcementId) return null;

  const statusUi = announcement ? apiStatusToUi(announcement.status) : "taslak";
  const headerTitle = (
    <span className={styles.headerTitle}>
      <span className={styles.headerIcon}>
        <DocumentModal width={24} height={24} color="#fff" />
      </span>
      <span className={styles.headerTitleText}>
        {announcement?.title ?? "—"}
      </span>
    </span>
  );

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={headerTitle}
      width="568px"
      position="right"
    >
      <div className={styles.drawerContent}>
        {isLoading && (
          <div className={styles.loadingState}>
            {t("common.loading")}
          </div>
        )}
        {error && <div className={styles.errorState}>{error}</div>}
        {!isLoading && !error && announcement && (
          <>
            <div className={styles.fieldRow}>
              <span className={styles.fieldLabel}>
                {t("announcements.detail.status")}
              </span>
              <Chip
                type={STATUS_CHIP_TYPE[statusUi]}
                size="small"
                variant="soft"
              >
                {STATUS_LABELS[statusUi] ?? statusUi}
              </Chip>
            </div>
            {announcement.imageFileID && (
              <div className={styles.imageWrap}>
                <FileImage
                  fileId={announcement.imageFileID}
                  alt=""
                  className={styles.image}
                  objectFit="contain"
                />
              </div>
            )}
            <div className={styles.dateRow}>
              <div className={styles.dateItem}>
                <Calendar width={18} height={18} className={styles.dateIcon} />
                <span className={styles.fieldLabel}>
                  {t("announcements.detail.startDate")}
                </span>
                <span className={styles.fieldValue}>
                  {formatDate(announcement.startDate)}
                </span>
              </div>
              <div className={styles.dateItem}>
                <Calendar width={18} height={18} className={styles.dateIcon} />
                <span className={styles.fieldLabel}>
                  {t("announcements.detail.endDate")}
                </span>
                <span className={styles.fieldValue}>
                  {formatDate(announcement.endDate)}
                </span>
              </div>
            </div>
            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>
                {t("announcements.detail.description")}
              </h4>
              <div className={styles.contentBox}>
                <p className={styles.sectionBody}>
                  {announcement.description || "—"}
                </p>
              </div>
            </div>
            <div className={styles.fieldRow}>
              <span className={styles.fieldLabel}>
                {t("announcements.detail.targetAudience")}
              </span>
              <span className={styles.fieldValue}>
                {announcement.targetAudience?.trim() ||
                  (announcement.targetAllStaff
                    ? i18n.t("announcementsCommon.allPersonnel")
                    : announcement.departments?.length
                      ? announcement.departments.map((d) => d.name).join(", ")
                      : "—")}
              </span>
            </div>
            {announcement.actionUrl && (
              <div className={styles.fieldRow}>
                <span className={styles.fieldLabel}>
                  {t("announcements.detail.actionUrl")}
                </span>
                <span className={styles.fieldValue}>{announcement.actionUrl}</span>
              </div>
            )}
            {announcement.actionText && (
              <div className={styles.fieldRow}>
                <span className={styles.fieldLabel}>
                  {t("announcements.detail.actionText")}
                </span>
                <span className={styles.fieldValue}>{announcement.actionText}</span>
              </div>
            )}
          </>
        )}
      </div>
    </Drawer>
  );
};

export default AnnouncementDetailDrawer;
